'use client';

import { fetcher } from '@insta-club/api';
import { InfoIcon } from '@insta-club/components';
import { Reel } from '@insta-club/models';
import { CustomVideo, ReelsDescription } from '@insta-club/ui';
import { formatDate } from '@insta-club/utilities';
import Image from 'next/image';
import Link from 'next/link';
import { useRef, useState } from 'react';
import 'swiper/css/pagination';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import ReelsMenu from './reels-menu';

interface Props {
    slide: Reel;
    muted: boolean;
    onToggleMute: (muted: boolean) => void;
}

const ReelsVideoPlayer: React.FC<Props> = ({ slide, muted = true, onToggleMute }) => {
    const viewRef = useRef(false);
    const [expanded, setExpanded] = useState(false);
    const filters = slide?.filters;
    const bottom = slide?.items?.length > 1 ? 'bottom-8' : 'bottom-1';

    const handleView = () => {
        if (viewRef.current == false) {
            fetcher(`/reels/toggle/${slide.id}/view`, 'dashboard', 'GET').then(() => {
                viewRef.current = true;
            });
        }
    };

    return (
        <div className='relative h-full w-full cursor-pointer bg-black'>
            {filters && filters.length > 0 ? (
                <div className='absolute top-4 right-4 left-12 z-50 flex flex-wrap gap-1'>
                    {filters?.map((filter, index) => {
                        return <span key={`fil-${index}`}>{filter.name}</span>;
                    })}
                </div>
            ) : null}
            <div
                onClick={() => setExpanded(false)}
                className={`absolute z-30 bg-gradient-to-t ${
                    expanded
                        ? `pointer-events-auto inset-x-0 ${bottom} top-0 from-black to-transparent`
                        : `pointer-events-none inset-x-0 ${bottom} h-1/3 from-black to-transparent`
                }`}
            />
            <Swiper pagination={true} modules={[Pagination]} className='reels-swiper h-full'>
                {slide?.items?.map((item, index) => {
                    return (
                        <SwiperSlide key={`ssi-${index}`}>
                            {item.type == 'image' ? (
                                <div className='flex h-full w-full items-center justify-center'>
                                    <Image
                                        alt={`reels-${item.id}`}
                                        src={item.url}
                                        width={0}
                                        height={0}
                                        sizes='100vw'
                                        style={{ width: '100%', height: 'auto' }}
                                    />
                                </div>
                            ) : (
                                <CustomVideo
                                    hlsUrl={item.hls}
                                    mp4Url={item.url}
                                    muted={muted}
                                    onView={handleView}
                                    onToggleMute={onToggleMute}
                                    autoPlayPaused
                                />
                            )}
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            <VideoExtra slide={slide} expanded={expanded} handleExpanded={setExpanded} />
        </div>
    );
};

export default ReelsVideoPlayer;

interface VideoExtraPrps {
    slide: Reel;
    expanded: boolean;
    handleExpanded: (expanded: boolean) => void;
}

const VideoExtra: React.FC<VideoExtraPrps> = ({ slide, expanded, handleExpanded }) => {
    return (
        <div className='absolute inset-x-0 bottom-8 z-50 flex px-4'>
            <div className='flex flex-1 flex-col gap-1 self-end pe-4 text-white'>
                {slide?.posts && slide.posts.length > 0 ? (
                    <div className='flex flex-wrap items-center gap-1'>
                        {slide?.posts?.map((post) => (
                            <Link
                                key={`p-${post?.id}`}
                                href={`/program/${post?.program_id}?day=${post?.day}`}
                                className='my-2'
                            >
                                <div className='bg-primary-50 text-primary-500 flex max-w-52 items-center gap-1 truncate rounded-2xl px-2 py-0.5 text-xs'>
                                    <InfoIcon width={12} height={12} />
                                    <span>{`مناسب پست ${formatDate(post?.posted_at, '%d %m')}`}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : slide?.post_types && slide.post_types.length > 0 ? (
                    <div className='flex flex-wrap items-center gap-1'>
                        {slide?.post_types?.map((post_type) => (
                            <div
                                className='flex max-w-52 items-center gap-1 truncate rounded-2xl bg-blue-50 px-2 py-0.5 text-xs text-blue-500'
                                key={`pt-${post_type?.id}`}
                            >
                                <InfoIcon width={12} height={12} />
                                <span>{`مناسب ${post_type.full_name}`}</span>
                            </div>
                        ))}
                    </div>
                ) : null}

                <ReelsDescription
                    text={slide.caption}
                    hashtags={slide.hashtags}
                    isExpanded={expanded}
                    onExpanded={handleExpanded}
                />
            </div>
            <ReelsMenu slide={slide} />
        </div>
    );
};

